import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Loader2, MapPin, Search, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Fix leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Auto-fit map to markers
function FitBounds({ coords }) {
  const map = useMap();
  useEffect(() => {
    if (coords.length > 0) {
      const bounds = L.latLngBounds(coords.map(c => [c.lat, c.lng]));
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [coords, map]);
  return null;
}

export default function MapPage({ category = "residenziale", readOnly = false }) {
  const [comuni, setComuni] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [coords, setCoords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [sugFilter, setSugFilter] = useState("");

  const isCommerciale = category === "commerciale";
  const title = isCommerciale ? "Zone Commerciale" : "Zone Residenziale";

  useEffect(() => {
    setSugFilter("");
    setAdding(null);
    setDeleting(null);
  }, [category]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [comuniRes, sugRes, coordsRes] = await Promise.all([
        axios.get(`${API}/comuni`),
        axios.get(`${API}/comuni/suggested?category=${category}`),
        axios.get(`${API}/comuni/coords?category=${category}`),
      ]);
      setComuni(comuniRes.data);
      setSuggested(sugRes.data);
      setCoords(coordsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [category]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addComune = async (nome) => {
    setAdding(nome);
    try {
      const res = await axios.post(`${API}/comuni`, { name: nome, category });
      toast.success(`${nome} aggiunto`);
      setComuni(prev => [...prev, res.data]);
      setSuggested(prev => prev.filter(s => s.nome.toLowerCase() !== nome.toLowerCase()));
      // Refresh coords
      const coordsRes = await axios.get(`${API}/comuni/coords?category=${category}`);
      setCoords(coordsRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    setAdding(null);
  };

  const deleteComune = async (id, name) => {
    if (!window.confirm(`Rimuovere ${name}?`)) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/comuni/${id}`);
      toast.success(`${name} rimosso`);
      setComuni(prev => prev.filter(c => c.id !== id));
      setCoords(prev => prev.filter(c => c.name.toLowerCase() !== name.toLowerCase()));
      const sugRes = await axios.get(`${API}/comuni/suggested?category=${category}`);
      setSuggested(sugRes.data);
    } catch (e) { toast.error("Errore"); }
    setDeleting(null);
  };

  const activeComuni = comuni.filter(c => (c.category || "residenziale") === category);
  const activeNamesInTab = new Set(activeComuni.map(c => c.name.toLowerCase()));
  const availableSuggested = suggested.filter(s => !activeNamesInTab.has(s.nome.toLowerCase()));
  const filteredSuggested = sugFilter
    ? availableSuggested.filter(s => s.nome.toLowerCase().includes(sugFilter.toLowerCase()))
    : availableSuggested;

  // Google Maps link with all active comuni
  const gmapsUrl = activeComuni.length > 0
    ? `https://www.google.com/maps/search/${encodeURIComponent(activeComuni.map(c => c.name).join("+"))}/@45.58,9.35,11z`
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div data-testid={`zone-${category}-page`} className="p-4 sm:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-4xl font-black text-zinc-900 tracking-tighter">{title}</h1>
          <p className="text-xs sm:text-sm text-zinc-500 mt-0.5">
            {activeComuni.length} comuni attivi
          </p>
        </div>
        {gmapsUrl && (
          <a href={gmapsUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs font-semibold text-zinc-500 hover:text-zinc-700 transition-colors">
            <ExternalLink className="w-3.5 h-3.5" /> Apri su Google Maps
          </a>
        )}
      </div>

      {/* Leaflet Map */}
      {coords.length > 0 && (
        <div className="border border-zinc-200 rounded-sm overflow-hidden" style={{ height: 350 }}>
          <MapContainer
            center={[45.58, 9.35]}
            zoom={11}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {coords.map(c => (
              <Marker key={c.name} position={[c.lat, c.lng]}>
                <Popup>
                  <strong>{c.name}</strong><br />
                  <span style={{ fontSize: 11, color: "#888" }}>{c.province}</span>
                </Popup>
              </Marker>
            ))}
            <FitBounds coords={coords} />
          </MapContainer>
        </div>
      )}

      {/* Active comuni chips */}
      <div>
        <h2 className="text-xs tracking-wider uppercase font-semibold text-zinc-400 mb-3">Comuni attivi</h2>
        {activeComuni.length === 0 ? (
          <div className="border border-zinc-200 bg-white rounded-sm p-6 text-center">
            <p className="text-sm text-zinc-500">Nessun comune attivo. Cerca e aggiungi qui sotto.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(
              activeComuni.sort((a, b) => a.name.localeCompare(b.name)).reduce((acc, c) => {
                const prov = c.province || "Altra";
                if (!acc[prov]) acc[prov] = [];
                acc[prov].push(c);
                return acc;
              }, {})
            ).sort(([a], [b]) => a.localeCompare(b))
              .map(([prov, items]) => (
                <div key={prov}>
                  <div className="mb-2">
                    <span className="text-[10px] tracking-wider uppercase font-bold text-zinc-500">{prov}</span>
                    <span className="text-[10px] text-zinc-300 ml-2">{items.length}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {items.map(c => (
                      <div key={c.id} data-testid={`active-comune-${c.id}`} className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-zinc-200 rounded-sm hover:border-zinc-300 group text-sm">
                        <MapPin className={`w-3 h-3 ${isCommerciale ? "text-blue-500" : "text-emerald-500"}`} />
                        <span className="font-medium text-zinc-800">{c.name}</span>
                        {!readOnly && (
                          <button
                            data-testid={`delete-comune-${c.id}`}
                            onClick={() => deleteComune(c.id, c.name)}
                            disabled={deleting === c.id}
                            className="ml-0.5 text-zinc-300 hover:text-red-500 transition-colors"
                          >
                            {deleting === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Search to add — Titolare only */}
      {!readOnly && (
      <div>
        <h2 className="text-xs tracking-wider uppercase font-semibold text-zinc-400 mb-3">Aggiungi comuni</h2>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
          <input
            data-testid="suggest-filter"
            value={sugFilter}
            onChange={e => setSugFilter(e.target.value)}
            placeholder="Cerca comune..."
            className="w-full h-9 pl-9 pr-3 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-zinc-400"
          />
        </div>
        {sugFilter.length >= 2 && (
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
            {filteredSuggested.slice(0, 30).map(s => (
              <button key={s.nome} data-testid={`suggest-${s.nome}`} onClick={() => addComune(s.nome)} disabled={adding === s.nome}
                className="flex items-center justify-between px-3 py-2 border border-zinc-200 bg-white rounded-sm text-left hover:border-zinc-400 hover:bg-zinc-50 transition-all group text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium text-zinc-700">{s.nome}</span>
                  <span className="text-[9px] text-zinc-400 shrink-0">{s.provincia_nome}</span>
                </div>
                {adding === s.nome ? <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-400 shrink-0" /> : <Plus className="w-3.5 h-3.5 text-zinc-300 group-hover:text-zinc-600 shrink-0" />}
              </button>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
